import { useEffect, useState } from "react";

type ModalScrollerMessageHandler = (event: MessageEvent) => void;

export const useBuiltInModalScroller = () => {
  const [pixels, setPixels] = useState(0);
  const [maxDialogHeight, setMaxDialogHeight] = useState<number | null>(null);

  useEffect(() => {
    const onMessage: ModalScrollerMessageHandler = (e) => {
      if (e.data?.type === "PARENT_SCROLL") {
        setPixels(e.data.scrollY ?? 0);
        if (typeof e.data.maxDialogHeight === "number") {
          setMaxDialogHeight(e.data.maxDialogHeight);
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const applyToDialog = (el: HTMLElement) => {
      el.style.top = `${pixels}px`;
      // Only the data-table pop-up opts into a viewport-bounded height so it
      // scrolls internally; other built-in dialogs keep their natural sizing.
      if (maxDialogHeight != null && el.closest(".table-form-popup")) {
        el.style.maxHeight = `${maxDialogHeight}px`;
      }
    };

    const applyLayout = (root: ParentNode | Document = document) => {
      root
        .querySelectorAll<HTMLElement>(".rodal-dialog")
        .forEach((el) => applyToDialog(el));
    };

    applyLayout();

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== "childList") continue;

        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.classList.contains("rodal-dialog")) {
            applyToDialog(node);
            return;
          }

          node
            .querySelectorAll<HTMLElement>(".rodal-dialog")
            .forEach((el) => applyToDialog(el));
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [pixels, maxDialogHeight]);
};
